import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';

/**
 * Navigation-state key that marks a router navigation as a deliberate in-app one
 * to a guarded appointments sub-page.
 *
 * The guard's original test is the PREVIOUS url, which answers "did the user come
 * from somewhere under /portal/appointments" — a good proxy while the dashboard
 * was the only way in, but it fails for any caller that legitimately links to
 * these pages from elsewhere in the portal. The Orbi panel is exactly that: it
 * floats over whatever page the user is on, so its "See All" landed on
 * /portal/appointments instead of the list it asked for whenever the panel was
 * opened from the dashboard, results, invoices — anywhere but appointments
 * (PCSA-13518 / PCSA-13522).
 *
 * A caller states its intent instead of being inferred from history:
 *
 *   router.navigate(['/portal/appointments/appointments-history'], {
 *     state: { [APPOINTMENTS_IN_APP_NAVIGATION]: true },
 *   });
 *
 * This is not a security boundary and was never one — the guard's job is keeping
 * a bookmarked or hand-typed deep link from landing on a list with no data loaded,
 * and navigation state is unreachable from a typed url or a bookmark (it lives in
 * the History entry the router itself pushes), so it draws that same line without
 * depending on where the user happened to be standing.
 */
export const APPOINTMENTS_IN_APP_NAVIGATION = 'appointmentsInAppNavigation';

@Injectable({
  providedIn: 'root',
})
export class AppointmentsGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate():
    | Observable<boolean | UrlTree>
    | Promise<boolean | UrlTree>
    | boolean
    | UrlTree {
    const currentNavigation = this.router.getCurrentNavigation();

    // An in-app caller that stated its intent (see APPOINTMENTS_IN_APP_NAVIGATION)
    // — checked before the referrer, since a caller like the Orbi panel is opened
    // over an arbitrary page and has no appointments url behind it.
    const state = currentNavigation?.extras?.state;
    if (state && state[APPOINTMENTS_IN_APP_NAVIGATION] === true) {
      return true;
    }

    // Check if we are coming from the dashboard component (or any other valid route)
    const previousNavigation = currentNavigation?.previousNavigation;

    // Allow navigation from valid routes like the dashboard page
    if (previousNavigation && previousNavigation.extractedUrl) {
      const previousUrl = previousNavigation.extractedUrl.toString();
      // If the previous URL is from a valid place, allow navigation
      if (previousUrl.includes('/portal/appointments')) {
        return true;
      }
    }

    // If no valid previous navigation (direct access), redirect to /portal/appointments
    return this.router.parseUrl('/portal/appointments');
  }
}
